import { createReducer } from '@reduxjs/toolkit';
import {
  actions as appStorageActions,
  constants as appStorageConstants,
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';

import constants from './constants';
import globalConstants from '../../constants';
import actions from './actions';
import operations from './operations';
import { ValueOf } from '../../GlobalTypes';

const { FETCH_FILES_URL_PARAMS } = constants;

interface FilesState {
  readonly currentPage: number;
  readonly groupId?: string;
  readonly hasError: boolean;
  readonly isLoading: boolean;
  readonly items: (appStorageRecords.AppStorageFile | undefined)[];
  readonly itemsPerPage: number;
  readonly kind?: ValueOf<typeof appStorageConstants.APP_STORAGE_KIND>;
  readonly totalItems: number;
}

const initialState: FilesState = {
  currentPage: 1,
  groupId: undefined,
  hasError: false,
  isLoading: false,
  items: [],
  itemsPerPage: globalConstants.TABLE_PAGE_SIZES[0],
  kind: undefined,
  totalItems: 0,
};

const deriveGroupId = (url: string | undefined): string | undefined => {
  const urlSearchParams = new URLSearchParams(url);
  const value = urlSearchParams.get(FETCH_FILES_URL_PARAMS.GROUP_ID);
  return value ?? undefined;
};

const filesReducer = createReducer(initialState, (builder) => {
  builder.addCase(actions.fetchFiles.pending, (draft, { meta }) => {
    if (meta.arg.groupId && meta.arg.groupId !== draft.groupId) {
      draft.items = [];
    }
    draft.isLoading = true;
  });

  builder.addCase(actions.fetchFiles.fulfilled, (draft, { payload }) => {
    draft.currentPage = payload.page;
    draft.groupId = deriveGroupId(payload.links.self);
    draft.hasError = false;
    draft.isLoading = false;
    draft.items = payload.items;
    draft.itemsPerPage = payload.per_page;
    draft.kind = payload.items[0]?.kind;
    draft.totalItems = payload.total_items;
  });
  builder.addCase(actions.fetchFiles.rejected, (draft) => {
    draft.hasError = true;
    draft.isLoading = false;
  });

  builder.addCase(operations.deleteAndRefreshFiles.pending, (draft) => {
    draft.isLoading = true;
  });

  builder.addCase(operations.deleteAndRefreshFiles.fulfilled, (draft) => {
    draft.isLoading = false;
  });

  builder.addCase(operations.deleteAndRefreshFiles.rejected, (draft) => {
    draft.isLoading = false;
  });

  builder.addCase(appStorageActions.updateFile.pending, (draft, { meta }) => {
    const {
      updatedFileProperties,
      unalteredFile,
      fileId,
    } = meta.arg;

    const affectedFileIndex = draft.items.findIndex((value) => value?.id === fileId);
    if (affectedFileIndex >= 0) {
      draft.items[affectedFileIndex] = new appStorageRecords.AppStorageFile({
        ...unalteredFile,
        description: updatedFileProperties.description ?? unalteredFile.description,
      });
    }
  });

  builder.addCase(appStorageActions.updateFile.fulfilled, (draft, { payload, meta }) => {
    const { fileId } = meta.arg;

    const affectedFileIndex = draft.items.findIndex((value) => value?.id === fileId);
    if (affectedFileIndex >= 0) {
      draft.items[affectedFileIndex] = payload.items[0];
    }
  });

  builder.addCase(appStorageActions.updateFile.rejected, (draft, { meta }) => {
    const {
      fileId,
      unalteredFile,
    } = meta.arg;

    const affectedFileIndex = draft.items.findIndex((value) => value?.id === fileId);
    if (affectedFileIndex >= 0) {
      draft.items[affectedFileIndex] = unalteredFile;
    }
  });
});

export default filesReducer;
export {
  deriveGroupId,
  FilesState,
  initialState,
};
